import { Select, notification } from "antd";
import color from "../../common/color";
import { useCallback, useEffect, useState } from "react";
import RetailersSearch, {
  selectedRetailersFieldToArray,
} from "./RetailersSearch";
import { fetchCountries, fetchUniversesWithRetailers } from "../../common/api";
import {
  trackUserPreference,
  trackUserPreferenceLater,
} from "../../common/full-story";
import TellUs from "./TellUs";
import alert from "../../common/alert";

const defaultMaxSearchableRetailers = 3;

function buildMoreAboutYouStyle(visible) {
  const style = { color: color.DarkGrey2 };
  if (!visible) {
    style.display = "none";
  }

  return style;
}

function getMaxSearchableRetailers(retailers) {
  return retailers
    ? Math.min(defaultMaxSearchableRetailers, retailers.length)
    : 0;
}

function getRetailers(universesWithRetailers, categoryId) {
  for (const universeWithRetailers of universesWithRetailers) {
    if (universeWithRetailers.universeId === categoryId) {
      return universeWithRetailers.retailers;
    }
  }

  return [];
}

function universesWithRetailersToCategoryOptions(universesWithRetailers) {
  return universesWithRetailers.map(({ universeId, universeName }) => {
    return {
      value: universeId,
      label: universeName,
    };
  });
}

function countriesToSelectElements(countries) {
  return countries.map(({ code, name }) => {
    return {
      value: code,
      label: name,
    };
  });
}

export default function MoreAboutYou({
  visible,
  disabled,
  onData,
  onActivity,
}) {
  const [api, contextHolder] = notification.useNotification();

  const [countryCode, setCountryCode] = useState(null);
  const [countryName, setCountryName] = useState(null);
  const [countryStatus, setCountryStatus] = useState(null);
  const [countryClicked, setCountryClicked] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countries, setCountries] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [universesWithRetailers, setUniversesWithRetailers] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [categoryState, setCategoryState] = useState();
  const [categoryClicked, setCategoryClicked] = useState(false);
  const [selectedRetailerIds, setSelectedRetailerIds] = useState([]);
  const [selectedRetailersDomains, setSelectedRetailersDomains] = useState([]);
  const [retailersDomainsWithIndex, setRetailersDomainsWithIndex] = useState(
    {}
  );

  const onCountryClicked = useCallback(() => setCountryClicked(true), []);

  useEffect(() => {
    if (!countryCode && countryClicked) {
      setCountryStatus("error");
    } else {
      setCountryStatus(null);
    }
  }, [countryCode, countryClicked]);

  useEffect(() => {
    if (categoryClicked && !categoryId) {
      setCategoryState("error");
    } else {
      setCategoryState(null);
    }
  }, [categoryClicked, categoryId]);

  useEffect(() => {
    setCategoryId(null);
    setCategoryClicked(false);

    if (countryCode) {
      setLoadingCategories(true);

      fetchUniversesWithRetailers(countryCode)
        .then((universesWithRetailers) =>
          setUniversesWithRetailers(universesWithRetailers)
        )
        .catch((err) => {
          const messageError = "Error occurred on fetching categories";
          console.error(`${messageError} : `, err);
          alert.error(api, messageError);
        })
        .finally(() => setLoadingCategories(false));
    }
  }, [countryCode, api]);

  useEffect(() => {
    setLoadingCountries(true);

    fetchCountries()
      .then((countries) => setCountries(countries))
      .catch((err) => {
        const messageError = "Error occurred on fetching countries";
        console.error(`${messageError} : `, err);
        api.error({
          message: "Error",
          description: messageError,
          placement: "top",
        });
      })
      .finally(() => setLoadingCountries(false));
  }, [api]);

  const onCategoryClicked = useCallback(() => {
    onActivity();
    setCategoryClicked(true);
  }, [onActivity]);

  const onSelectCategory = useCallback(
    (input, option) => {
      onActivity();
      setCategoryId(input);
      setCategoryName(option.label);
      onData({
        countryCode,
        countryName,
        categoryId,
        categoryName,
        retailerIds: selectedRetailerIds,
        retailersDomains: selectedRetailersDomains,
      });
    },
    [
      countryCode,
      selectedRetailerIds,
      categoryId,
      categoryName,
      countryName,
      selectedRetailersDomains,
      onData,
      onActivity,
    ]
  );

  const onRetailerIdsSelected = useCallback(
    (retailerIds, retailersDomains, retailersDomainsWithIndex) => {
      onActivity();
      setSelectedRetailerIds(retailerIds);
      setSelectedRetailersDomains(retailersDomains);
      setRetailersDomainsWithIndex(retailersDomainsWithIndex);
      onData({
        countryCode,
        countryName,
        categoryId,
        categoryName,
        retailerIds,
        retailersDomains,
      });
      trackUserPreference(countryName, categoryName, retailersDomains);
    },
    [countryCode, categoryId, categoryName, countryName, onData, onActivity]
  );

  const filterCountries = useCallback((input, option) => {
    return option.label.toLowerCase().includes(input.toLowerCase());
  }, []);

  const filterCategories = useCallback((input, option) => {
    return option.label.toLowerCase().includes(input.toLowerCase());
  }, []);

  const onSelectCountry = useCallback(
    (input, option) => {
      onActivity();
      setCountryCode(input);
      setCountryName(option.label);
    },
    [onActivity]
  );

  const onSearchRetailer = useCallback(
    (retailer, index) => {
      onActivity();
      const allRetailersDomainsWithIndex = { ...retailersDomainsWithIndex };
      allRetailersDomainsWithIndex[`${index}`] = retailer;

      trackUserPreferenceLater(
        countryName,
        categoryName,
        selectedRetailersFieldToArray(allRetailersDomainsWithIndex)
      );
    },
    [countryName, categoryName, retailersDomainsWithIndex, onActivity]
  );

  function buildRetailersSearch() {
    const retailers = getRetailers(universesWithRetailers, categoryId);
    const maxSearchableRetailers = getMaxSearchableRetailers(retailers);

    return maxSearchableRetailers > 0 ? (
      <>
        <div>
          {maxSearchableRetailers === 2
            ? "Select 2 retailer websites to track"
            : "Select at least 2 retailer websites to track"}
        </div>
        <div className="mt-1">
          <RetailersSearch
            maxSearchableRetailers={maxSearchableRetailers}
            retailers={retailers}
            disabled={disabled}
            onRetailerIdsSelected={onRetailerIdsSelected}
            onSearchRetailer={onSearchRetailer}
          />
        </div>
      </>
    ) : null;
  }

  const trackFullStoryEvent = useCallback(
    () =>
      trackUserPreference(countryName, categoryName, selectedRetailersDomains),
    [countryName, categoryName, selectedRetailersDomains]
  );

  const onSearchCountry = useCallback(
    (countryName) => {
      onActivity();
      trackUserPreferenceLater(
        countryName,
        categoryName,
        selectedRetailersDomains
      );
    },
    [categoryName, selectedRetailersDomains, onActivity]
  );

  const onSearchCategory = useCallback(
    (categoryName) => {
      onActivity();
      trackUserPreferenceLater(
        countryName,
        categoryName,
        selectedRetailersDomains
      );
    },
    [countryName, selectedRetailersDomains, onActivity]
  );

  return (
    <div
      className="font-medium text-[16px]"
      style={buildMoreAboutYouStyle(visible)}
    >
      {contextHolder}
      <div className="mt-5">
        <div>What country do you operate in?</div>
        <div className="mt-1 lpc-select">
          <Select
            size="large"
            loading={loadingCountries}
            disabled={disabled || loadingCountries || !countries.length}
            options={countriesToSelectElements(countries)}
            status={countryStatus}
            showSearch
            filterOption={filterCountries}
            onSelect={onSelectCountry}
            onClick={onCountryClicked}
            onBlur={trackFullStoryEvent}
            onSearch={onSearchCountry}
            autoComplete="none"
            className="w-full"
          />
        </div>
      </div>
      <div className="mt-5">
        <div>What product category would you like to track?</div>
        <div className="mt-1 lpc-select">
          <Select
            size="large"
            loading={loadingCategories}
            showSearch
            value={categoryId}
            disabled={
              disabled || loadingCategories || !universesWithRetailers.length
            }
            options={universesWithRetailersToCategoryOptions(
              universesWithRetailers
            )}
            status={categoryState}
            filterOption={filterCategories}
            onSelect={onSelectCategory}
            onClick={onCategoryClicked}
            onBlur={trackFullStoryEvent}
            onSearch={onSearchCategory}
            autoComplete="none"
            className="w-full"
          />
        </div>
      </div>
      {countryCode && !loadingCategories ? (
        <div className="mt-5">{buildRetailersSearch()}</div>
      ) : null}
      <div className="mt-5">
        <TellUs onActivity={onActivity} />
      </div>
    </div>
  );
}
